import React, { useState, useEffect, useCallback } from 'react';
import Sidebar from '../components/Sidebar';

const token = () => localStorage.getItem('token');

// Display order + grouping for the known label keys
const GROUPS = [
  { title: 'Platform', keys: ['platform_owner'] },
  { title: 'Broker', keys: ['broker.company', 'broker.manager', 'broker.adviser'] },
  { title: 'Introducer', keys: ['introducer.company', 'introducer.manager', 'introducer.adviser'] },
];

export default function OwnerRoleLabels() {
  const [labels, setLabels] = useState({});
  const [original, setOriginal] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/role-labels', {
        headers: { Authorization: `Bearer ${token()}` },
      });
      if (res.status === 401 || res.status === 403) {
        setError('Only the platform owner can manage role labels.');
        return;
      }
      if (!res.ok) { setError('Failed to load labels.'); return; }
      const data = await res.json();
      setLabels(data.labels || {});
      setOriginal(data.labels || {});
      setError('');
    } catch (e) {
      setError('Error loading labels: ' + e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const dirty = JSON.stringify(labels) !== JSON.stringify(original);

  const save = async () => {
    setSaving(true); setSuccess(''); setError('');
    try {
      const res = await fetch('/api/role-labels', {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ labels }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        setError(d.error || 'Failed to save labels.');
        return;
      }
      const data = await res.json();
      setLabels(data.labels || labels);
      setOriginal(data.labels || labels);
      setSuccess('Labels saved. Changes appear across the platform on next page load.');
    } catch (e) {
      setError('Error saving: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  const allKeys = GROUPS.flatMap(g => g.keys);
  const extraKeys = Object.keys(labels).filter(k => !allKeys.includes(k));

  const card = { background: '#fff', borderRadius: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', padding: 24, marginBottom: 20, maxWidth: 640 };
  const groupTitle = { fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#64748b', margin: '0 0 12px' };
  const row = { display: 'flex', alignItems: 'center', gap: 16, marginBottom: 12 };
  const keyStyle = { width: 180, fontSize: 12, color: '#94a3b8', fontFamily: 'monospace', flexShrink: 0 };
  const input = { flex: 1, padding: '9px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14, color: '#0e1b2c', outline: 'none', boxSizing: 'border-box' };

  const renderRow = (key) => (
    <div style={row} key={key}>
      <span style={keyStyle}>{key}</span>
      <input
        style={input}
        value={labels[key] ?? ''}
        onChange={e => setLabels(prev => ({ ...prev, [key]: e.target.value }))}
        placeholder={key}
      />
    </div>
  );

  return (
    <div className="dashboard-container">
      <Sidebar />
      <main className="dashboard-main">
        <div style={{ marginBottom: 24 }}>
          <h1 style={{ margin: '0 0 6px', fontSize: 24, color: '#0e1b2c' }}>Role Labels</h1>
          <p style={{ margin: 0, color: '#64748b', fontSize: 14 }}>
            Edit the names shown across the platform for each role tier. The underlying
            data is unchanged — only the displayed wording is updated.
          </p>
        </div>

        {error && <div style={{ ...card, background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca' }}>{error}</div>}
        {success && <div style={{ ...card, background: '#f0fdf4', color: '#166534', border: '1px solid #bbf7d0' }}>{success}</div>}

        {loading ? (
          <div style={card}>Loading…</div>
        ) : (
          <>
            {GROUPS.map(g => (
              <div style={card} key={g.title}>
                <p style={groupTitle}>{g.title}</p>
                {g.keys.filter(k => k in labels).map(renderRow)}
              </div>
            ))}
            {extraKeys.length > 0 && (
              <div style={card}>
                <p style={groupTitle}>Other</p>
                {extraKeys.map(renderRow)}
              </div>
            )}
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', maxWidth: 640 }}>
              <button
                onClick={save}
                disabled={!dirty || saving}
                style={{
                  padding: '10px 22px', borderRadius: 8, border: 'none', fontSize: 14, fontWeight: 600,
                  cursor: (!dirty || saving) ? 'not-allowed' : 'pointer',
                  background: (!dirty || saving) ? '#cbd5e1' : '#0e1b2c', color: '#fff',
                }}
              >
                {saving ? 'Saving…' : 'Save changes'}
              </button>
              {dirty && !saving && (
                <button
                  onClick={() => { setLabels(original); setSuccess(''); setError(''); }}
                  style={{ padding: '10px 22px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer', color: '#475569' }}
                >
                  Reset
                </button>
              )}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
